import React, { useEffect, useRef, useState } from 'react';
import api from '../../../adapters/axios';
import PortalDropdown from './Portalropdown';
import '../AlertMenu/AlertMenu';

interface Subscription {
  type: 'Tag' | 'Company';
  name: string;
  frequency: 'Daily' | 'Weekly' | 'Never';
}

const AlertMenu = () => {
  const [tags, setTags] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);

  const [dbOpen, setDbOpen] = useState(true);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<'Never' | 'Daily' | 'Weekly'>('Never');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  const [ddOpen, setDdOpen] = useState(true);
  const [ddSelectedTags, setDdSelectedTags] = useState<string[]>([]);
  const [ddFrequency, setDdFrequency] = useState<'Never' | 'Daily' | 'Weekly'>('Never');
  const [ddSubscriptions, setDdSubscriptions] = useState<Subscription[]>([]);
  const [ddShowTagDropdown, setDdShowTagDropdown] = useState(false);
  const [ddTagSearch, setDdTagSearch] = useState('');
  const [userTags, setUserTags] = useState<{ tagName: string, id: number }[]>([]);

  // anchors used to position portal dropdowns (escapes ancestor clipping)
  const tagInputRef = useRef<HTMLInputElement>(null);
  const companyInputRef = useRef<HTMLInputElement>(null);
  const ddTagInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchTags();
    fetchCompanies();
    fetchSubscriptions();
    fetchDdSubscriptions();
    fetchUserTags();
  }, []);

  const fetchTags = async () => {
    try {
      const res = await api.get('/getTags');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid tag response');
      setTags(data.map((t: any) => t.tagName));
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchUserTags = async () => {
    try {
      const res = await api.get('/user-tags/company');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid tag response');
      setUserTags(data.map((t: any) => ({ tagName: t.tagName, id: t.id })));
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/getCompanies');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid company response');
      setCompanies(data.map((c: any) => c.companyName));
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await api.get('/email-alerts');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid subscriptions');
      setSubscriptions(data);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    }
  };

  const fetchDdSubscriptions = async () => {
    try {
      const res = await api.get('/drag-email-alerts');
      const data = res.data;
      if (!Array.isArray(data)) throw new Error('Invalid dd subscriptions');
      setDdSubscriptions(data);
    } catch (err) {
      console.error('Error fetching drag-drop subscriptions:', err);
    }
  };

  const handleFrequencyChange = async (
    sub: Subscription,
    newFrequency: 'Never' | 'Daily' | 'Weekly',
    section: 'db' | 'dd' = 'db'
  ) => {
    try {
      const endpoint = section === 'dd' ? '/drag-email-alerts/frequency' : '/email-alerts/frequency';
      await api.patch(endpoint, { type: sub.type, name: sub.name, frequency: newFrequency });
      section === 'dd' ? fetchDdSubscriptions() : fetchSubscriptions();
    } catch (err) {
      console.error('Error updating frequency:', err);
    }
  };

  const handleUnsubscribe = async (sub: Subscription, section: 'db' | 'dd' = 'db') => {
    try {
      const endpoint = section === 'dd' ? '/drag-email-alerts/unsubscribe' : '/email-alerts/unsubscribe';
      await api.delete(endpoint, { data: { type: sub.type, name: sub.name } });
      section === 'dd' ? fetchDdSubscriptions() : fetchSubscriptions();
    } catch (err) {
      console.error('Error unsubscribing:', err);
    }
  };

  const handleSubscribe = async () => {
    const newSubs: Subscription[] = [
      ...selectedTags.map(tag => ({ type: 'Tag' as const, name: tag, frequency })),
      ...selectedCompanies.map(company => ({ type: 'Company' as const, name: company, frequency })),
    ];
    try {
      await api.post('/email-alerts/subscribe', newSubs);
      await fetchSubscriptions();
      setSelectedTags([]);
      setSelectedCompanies([]);
      setFrequency('Never');
    } catch (err) {
      console.error('Error subscribing:', err);
    }
  };

  const handleDdSubscribe = async () => {
    const newSubs = ddSelectedTags.map(tag => ({
      type: 'Tag' as const,
      name: tag,
      tagId: userTags.find(t => t.tagName === tag)?.id,
      frequency: ddFrequency,
    }));
    try {
      await api.post('/drag-email-alerts/subscribe', newSubs);
      await fetchDdSubscriptions();
      setDdSelectedTags([]);
      setDdFrequency('Never');
    } catch (err) {
      console.error('Error subscribing drag-drop:', err);
    }
  };

  const renderSubscriptionTable = (subs: Subscription[], section: 'db' | 'dd') => (
    <div className="subscription-list">
      <h3>Your Subscriptions</h3>
      <table className="subscription-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Name</th>
            <th>Email Frequency</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {subs.map((sub, idx) => (
            <tr key={idx}>
              <td>{sub.type}</td>
              <td className="text-blue">{sub.name}</td>
              <td>
                <select
                  value={sub.frequency}
                  onChange={(e) =>
                    handleFrequencyChange(sub, e.target.value as 'Never' | 'Daily' | 'Weekly', section)
                  }
                >
                  <option value="Never">Never</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </td>
              <td>
                <button
                  className="unsubscribe-btn blue-btn"
                  onClick={() => handleUnsubscribe(sub, section)}
                >
                  Unsubscribe
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="alert-page-wrapper">
      <h2><span className="alert-heading">Alert Notifications</span></h2>

      {/* ── Section 1: WendWise DB Alerts ── */}
      <div className="alert-section">
        <button className="section-header" onClick={() => setDbOpen(o => !o)}>
          <span> Wendwise Research Alerts</span>
          <span className="chevron">{dbOpen ? '▲' : '▼'}</span>
        </button>

        {dbOpen && (
          <div className="section-body">
            <div className="alert-form">

              {/* Tags */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Tags</label>
                <input
                  ref={tagInputRef}
                  type="text"
                  placeholder="Search & select tags..."
                  className="small-input"
                  value={tagSearch}
                  onFocus={() => setShowTagDropdown(true)}
                  onChange={(e) => { setTagSearch(e.target.value); setShowTagDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                />
                {showTagDropdown && (
                  <PortalDropdown anchorRef={tagInputRef}>
                    {tags
                      .filter(tag => tag.toLowerCase().includes(tagSearch.toLowerCase()) && !selectedTags.includes(tag))
                      .map(tag => (
                        <div key={tag} className="dropdown-item"
                          onMouseDown={() => { setSelectedTags(prev => [...prev, tag]); setTagSearch(''); }}>
                          {tag}
                        </div>
                      ))}
                  </PortalDropdown>
                )}
                {selectedTags.length > 0 && (
                  <div className="selected-bar">
                    {selectedTags.map(tag => (
                      <span key={tag} className="selected-chip">
                        {tag}
                        <button className="remove-chip" onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Companies */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Companies</label>
                <input
                  ref={companyInputRef}
                  type="text"
                  placeholder="Search & select companies..."
                  className="small-input"
                  value={companySearch}
                  onFocus={() => setShowCompanyDropdown(true)}
                  onChange={(e) => { setCompanySearch(e.target.value); setShowCompanyDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                />
                {showCompanyDropdown && (
                  <PortalDropdown anchorRef={companyInputRef}>
                    {companies
                      .filter(c => c.toLowerCase().includes(companySearch.toLowerCase()) && !selectedCompanies.includes(c))
                      .map(c => (
                        <div key={c} className="dropdown-item"
                          onMouseDown={() => { setSelectedCompanies(prev => [...prev, c]); setCompanySearch(''); }}>
                          {c}
                        </div>
                      ))}
                  </PortalDropdown>
                )}
                {selectedCompanies.length > 0 && (
                  <div className="selected-bar">
                    {selectedCompanies.map(c => (
                      <span key={c} className="selected-chip">
                        {c}
                        <button className="remove-chip" onClick={() => setSelectedCompanies(prev => prev.filter(x => x !== c))}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Frequency + Subscribe inline */}
              <div className="form-group freq-subscribe-row">
                <label>Frequency</label>
                <div className="freq-subscribe-inline">
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as any)}
                    className="small-select"
                  >
                    <option value="Never">Never</option>
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                  </select>
                  <button className="subscribe-btn" onClick={handleSubscribe}>🔔 Subscribe</button>
                </div>
              </div>

            </div>

            {subscriptions.length > 0 && renderSubscriptionTable(subscriptions, 'db')}
          </div>
        )}
      </div>

      {/* ── Empty state when both collapsed ── */}
      {!dbOpen && !ddOpen && (
        <div className="alert-empty-state">
          <span className="empty-icon">🔔</span>
          <strong>No sections expanded</strong>
          <p>Click a section above to configure your alert subscriptions.</p>
        </div>
      )}

      {/* ── Section 2: Drag & Drop Alerts ── */}
      <div className="alert-section">
        <button className="section-header" onClick={() => setDdOpen(o => !o)}>
          <span> Drag &amp; Drop Alerts</span>
          <span className="chevron">{ddOpen ? '▲' : '▼'}</span>
        </button>

        {ddOpen && (
          <div className="section-body">
            <div className="alert-form">

              {/* User Tags */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>User Tags</label>
                <input
                  ref={ddTagInputRef}
                  type="text"
                  placeholder="Search & select tags..."
                  className="small-input"
                  value={ddTagSearch}
                  onFocus={() => setDdShowTagDropdown(true)}
                  onChange={(e) => { setDdTagSearch(e.target.value); setDdShowTagDropdown(true); }}
                  onBlur={() => setTimeout(() => setDdShowTagDropdown(false), 200)}
                />
                {ddShowTagDropdown && (
                  <PortalDropdown anchorRef={ddTagInputRef}>
                    {userTags
                      .filter(tag => tag.tagName.toLowerCase().includes(ddTagSearch.toLowerCase()) && !ddSelectedTags.includes(tag.tagName))
                      .map(tag => (
                        <div key={tag.id} className="dropdown-item"
                          onMouseDown={() => { setDdSelectedTags(prev => [...prev, tag.tagName]); setDdTagSearch(''); }}>
                          {tag.tagName}
                        </div>
                      ))}
                  </PortalDropdown>
                )}
                {ddSelectedTags.length > 0 && (
                  <div className="selected-bar">
                    {ddSelectedTags.map(tag => (
                      <span key={tag} className="selected-chip">
                        {tag}
                        <button className="remove-chip" onClick={() => setDdSelectedTags(prev => prev.filter(t => t !== tag))}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Frequency + Subscribe inline */}
              <div className="form-group freq-subscribe-row">
                <label>Frequency</label>
                <div className="freq-subscribe-inline">
                  <select
                    value={ddFrequency}
                    onChange={(e) => setDdFrequency(e.target.value as any)}
                    className="small-select"
                  >
                    <option value="Never">Never</option>
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                  </select>
                  <button className="subscribe-btn" onClick={handleDdSubscribe}>🔔 Subscribe</button>
                </div>
              </div>

            </div>

            {ddSubscriptions.length > 0 && renderSubscriptionTable(ddSubscriptions, 'dd')}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertMenu;